import type { Persona } from '@/payload-types'

/** A single "How We Help" row as authored on a persona. */
export type HowWeHelpSection = NonNullable<Persona['howWeHelpSections']>[number]

export interface SectionButton {
  label: string
  href: string
  /** True when the link leaves the site, so it opens in a new tab. */
  isExternal: boolean
}

/** Hosts we treat as our own, so an editor pasting a full URL still gets an in-app link. */
const OWN_HOSTS = ['serottelaw.com', 'www.serottelaw.com']

/** Protocols an editor can usefully link to. Anything else (javascript:, data:) is dropped. */
const LINKABLE_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:']

/**
 * Turns the CMS button fields on a "How We Help" section into something renderable,
 * or null when the section has no usable button. Both a label and a link are
 * required: a half-configured section renders exactly as it did before, with no
 * empty button or leftover spacing.
 *
 * Shared by the section banner and the section content block so both instances
 * always agree on label and destination.
 */
export function resolveSectionButton(
  section: Pick<HowWeHelpSection, 'buttonText' | 'buttonLink'> | null | undefined,
): SectionButton | null {
  const label = section?.buttonText?.trim()
  const href = section?.buttonLink?.trim()
  if (!label || !href) return null

  // Same-page anchors and site-relative paths need no parsing. A protocol-relative
  // "//host" URL looks relative but isn't, so it goes down the absolute path below.
  if (href.startsWith('#') || (href.startsWith('/') && !href.startsWith('//'))) {
    return { label, href, isExternal: false }
  }

  let url: URL
  try {
    url = new URL(href.startsWith('//') ? `https:${href}` : href)
  } catch {
    return null
  }

  if (!LINKABLE_PROTOCOLS.includes(url.protocol)) return null

  // mailto:/tel: links hand off to another app rather than opening a tab.
  if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
    return { label, href: url.href, isExternal: false }
  }

  if (OWN_HOSTS.includes(url.hostname.toLowerCase())) {
    return { label, href: `${url.pathname}${url.search}${url.hash}`, isExternal: false }
  }

  return { label, href: url.href, isExternal: true }
}
